"use client";

import { c as _c } from "react/compiler-runtime";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { getSession, signIn } from "next-auth/react";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useFirebaseMessaging } from "@/hooks/use-firebase-messaging";
import { useLanguage } from "@/contexts/LanguageContext";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { PasswordInput } from "@/components/ui/password-input";
import GoogleSocial from "../GoogleSocial";
const loginSchema = z.object({
  email: z.string().min(1, {
    message: "validation.required"
  }).email({
    message: "validation.email_invalid"
  }),
  password: z.string().min(1, {
    message: "validation.required"
  }),
  remember: z.boolean().optional()
});
type LoginFormData = z.infer<typeof loginSchema>;
export default function SignInPage() {
  const $ = _c(102);
  const router = useRouter();
  const {
    t
  } = useLanguage();
  const {
    fcmToken
  } = useFirebaseMessaging();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      resolver: zodResolver(loginSchema)
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    register,
    handleSubmit,
    control,
    formState: t1
  } = useForm(t0);
  const {
    errors
  } = t1;
  let t2;
  if ($[1] !== fcmToken) {
    t2 = async data => {
      const authPayload = {
        email: data.email || "",
        password: data.password || "",
        "fcm-token": fcmToken || "",
        redirect: false,
        remember: data.remember ?? false
      };
      const result = await signIn("credentials", authPayload);
      if (result?.error) {
        throw new Error(result.error);
      }
      return result;
    };
    $[1] = fcmToken;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== router || $[4] !== t) {
    t3 = async () => {
      toast.success(t("auth.signin.success"));
      const session = await getSession();
      const role = session?.user?.role_name;
      router.push(`/${role}/home`);
    };
    $[3] = router;
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      mutationFn: t2,
      onSuccess: t3,
      onError(error) {
        toast.error(error.message || t("empty_states.error.message"));
      }
    };
    $[6] = t;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const mutation = useMutation(t4);
  let t5;
  if ($[10] !== mutation) {
    t5 = data_0 => {
      mutation.mutate(data_0);
    };
    $[10] = mutation;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleFormSubmit = t5;
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="flex justify-center mb-1"><img src="/logo.svg" alt="Adviser Logo" width={140} height={48} className="object-cover" /></div>;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t) {
    t7 = t("auth.signin.title");
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t7) {
    t8 = <h1 className="text-xl font-bold text-[#15042B]">{t7}</h1>;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t) {
    t9 = errs => {
      const firstErrorKey = Object.keys(errs)[0];
      if (firstErrorKey) {
        const errorObj = errs[firstErrorKey as keyof typeof errs];
        if (errorObj?.message) {
          toast.error(t(errorObj.message));
        }
        const errorElement = document.getElementById(firstErrorKey) || document.getElementsByName(firstErrorKey)[0];
        if (errorElement) {
          errorElement.focus();
          errorElement.scrollIntoView({
            behavior: "smooth",
            block: "center"
          });
        }
      }
    };
    $[17] = t;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== handleFormSubmit || $[20] !== handleSubmit || $[21] !== t9) {
    t10 = handleSubmit(handleFormSubmit, t9);
    $[19] = handleFormSubmit;
    $[20] = handleSubmit;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t) {
    t11 = t("auth.signin.email");
    $[23] = t;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <span className="text-red-500">*</span>;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t11) {
    t13 = <Label htmlFor="email" className="text-[#15042B] text-sm font-semibold leading-5">{t11} {t12}</Label>;
    $[26] = t11;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t) {
    t14 = t("auth.signin.emailPlaceholder");
    $[28] = t;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== register) {
    t15 = register("email");
    $[30] = register;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const t16 = !!errors.email;
  let t17;
  if ($[32] !== t14 || $[33] !== t15 || $[34] !== t16) {
    t17 = <Input id="email" type="email" placeholder={t14} {...t15} error={t16} />;
    $[32] = t14;
    $[33] = t15;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== errors.email || $[37] !== t) {
    t18 = errors.email && <p className="text-xs text-red-500">{t(errors.email.message || "")}</p>;
    $[36] = errors.email;
    $[37] = t;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t13 || $[40] !== t17 || $[41] !== t18) {
    t19 = <div className="flex flex-col gap-2">{t13}{t17}{t18}</div>;
    $[39] = t13;
    $[40] = t17;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t) {
    t20 = t("auth.signin.password");
    $[43] = t;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <span className="text-red-500">*</span>;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t20) {
    t22 = <Label htmlFor="password" className="text-[#15042B] text-sm font-semibold leading-5">{t20} {t21}</Label>;
    $[46] = t20;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== register) {
    t23 = register("password");
    $[48] = register;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  const t24 = !!errors.password;
  let t25;
  if ($[50] !== t23 || $[51] !== t24) {
    t25 = <PasswordInput id="password" type="password" placeholder="password" {...t23} error={t24} />;
    $[50] = t23;
    $[51] = t24;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] !== errors.password || $[54] !== t) {
    t26 = errors.password && <p className="text-xs text-red-500">{t(errors.password.message || "")}</p>;
    $[53] = errors.password;
    $[54] = t;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== t22 || $[57] !== t25 || $[58] !== t26) {
    t27 = <div className="flex flex-col gap-2">{t22}{t25}{t26}</div>;
    $[56] = t22;
    $[57] = t25;
    $[58] = t26;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== control) {
    t28 = <Controller name="remember" control={control} defaultValue={false} render={_temp} />;
    $[60] = control;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t) {
    t29 = t("auth.signin.rememberMe");
    $[62] = t;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== t29) {
    t30 = <label htmlFor="remember" className="text-sm font-medium text-gray-600 cursor-pointer select-none">{t29}</label>;
    $[64] = t29;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  let t31;
  if ($[66] !== t28 || $[67] !== t30) {
    t31 = <div className="flex items-center space-x-2">{t28}{t30}</div>;
    $[66] = t28;
    $[67] = t30;
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  let t32;
  if ($[69] !== t) {
    t32 = t("auth.signin.forgotPassword");
    $[69] = t;
    $[70] = t32;
  } else {
    t32 = $[70];
  }
  let t33;
  if ($[71] !== t32) {
    t33 = <Link href="/auth/forgot-password" className="text-sm auth-link">{t32}</Link>;
    $[71] = t32;
    $[72] = t33;
  } else {
    t33 = $[72];
  }
  let t34;
  if ($[73] !== t31 || $[74] !== t33) {
    t34 = <div className="flex items-center justify-between mt-1">{t31}{t33}</div>;
    $[73] = t31;
    $[74] = t33;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  let t35;
  if ($[76] !== errors.root || $[77] !== t) {
    t35 = errors.root && <p className="text-sm text-red-500 text-center">{t(errors.root.message || "")}</p>;
    $[76] = errors.root;
    $[77] = t;
    $[78] = t35;
  } else {
    t35 = $[78];
  }
  let t36;
  if ($[79] !== mutation.isPending || $[80] !== t) {
    t36 = mutation.isPending ? t("auth.signin.submitting") : t("auth.signin.submit");
    $[79] = mutation.isPending;
    $[80] = t;
    $[81] = t36;
  } else {
    t36 = $[81];
  }
  let t37;
  if ($[82] !== mutation.isPending || $[83] !== t36) {
    t37 = <Button type="submit" disabled={mutation.isPending} className="w-full bg-[#008081] hover:bg-[#008081] text-white h-12 text-base font-semibold rounded-xl shadow-md transition-all active:scale-[0.98]">{t36}</Button>;
    $[82] = mutation.isPending;
    $[83] = t36;
    $[84] = t37;
  } else {
    t37 = $[84];
  }
  let t38;
  if ($[85] !== t10 || $[86] !== t19 || $[87] !== t27 || $[88] !== t34 || $[89] !== t35 || $[90] !== t37) {
    t38 = <form onSubmit={t10} className="flex flex-col gap-5">{t19}{t27}{t34}{t35}{t37}</form>;
    $[85] = t10;
    $[86] = t19;
    $[87] = t27;
    $[88] = t34;
    $[89] = t35;
    $[90] = t37;
    $[91] = t38;
  } else {
    t38 = $[91];
  }
  let t39;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>;
    $[92] = t39;
  } else {
    t39 = $[92];
  }
  let t40;
  if ($[93] !== t) {
    t40 = t("auth.signin.or");
    $[93] = t;
    $[94] = t40;
  } else {
    t40 = $[94];
  }
  let t41;
  if ($[95] !== t40) {
    t41 = <div className="relative flex items-center justify-center my-1">{t39}<span className="relative bg-white px-3 text-xs text-gray-400 font-medium">{t40}</span></div>;
    $[95] = t40;
    $[96] = t41;
  } else {
    t41 = $[96];
  }
  let t42;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <GoogleSocial />;
    $[97] = t42;
  } else {
    t42 = $[97];
  }
  let t43;
  if ($[98] !== t38 || $[99] !== t41 || $[100] !== t8) {
    t43 = <div className="w-full max-w-[500px] bg-white rounded-[24px] shadow-2xl p-8 md:p-10 flex flex-col gap-6">{t6}{t8}{t38}{t41}{t42}</div>;
    $[98] = t38;
    $[99] = t41;
    $[100] = t8;
    $[101] = t43;
  } else {
    t43 = $[101];
  }
  return t43;
}
function _temp(t0) {
  const {
    field
  } = t0;
  return <Checkbox id="remember" checked={field.value} onCheckedChange={field.onChange} />;
}